import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { first, map, Subscription, switchMap, distinctUntilChanged } from 'rxjs';
import { NgbModal, NgbTooltip, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';

import { AnalyticsService } from '../../shared/services/analytics.service';
import { ConversationService } from '../../shared/services/conversation.service';
import { DiscoverService } from '../../shared/services/discover.service';
import { MediaService } from '../../shared/services/media.service';
import { StatsigService } from '../../shared/services/statsig.service';
import { TokensService } from '../../shared/services/tokens.service';
import { ProfileStateService } from 'app/shared/services/profile-state.service';

import { CreatorsCarouselComponent } from '../../components/image-generation/creators-carousel/creators-carousel.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { EmptyStateComponent } from '../../components/empty-state/empty-state.component';
import { ImagePresetsComponent } from 'app/components/image-generation/image-presets/image-presets.component';
import { MediaLightboxComponent } from '../../components/media-lightbox/media-lightbox.component';
import { RegenerateImageModalComponent } from 'app/components/image-generation/regenerate-image-modal/regenerate-image-modal.component';
import { SuggestedPromptComponent } from '../../components/suggested-prompt/suggested-prompt.component';
import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { ThumbnailLoaderComponent } from '../../components/image-generation/thumbnail-loader/thumbnail-loader.component';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { ImageComponent } from '../../shared/components/image/image.component';

import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';

import { IButtonConfig } from 'app/shared/interfaces/button.interfaces';
import { ICreatorPromptResponse, IPrompt } from '../../interfaces/conversations.interfaces';
import { IDiscoverItem, IDiscoverResponse } from '../../interfaces/discover.interfaces';
import { IEmptyStateConfig } from '../../shared/interfaces/shared.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import { IToastConfig } from '../../shared/interfaces/toast-config.interfaces';
import { IGenerateImage } from '../../shared/interfaces/media.interfaces';
import { IPresetData } from 'app/interfaces/image-generation.interfaces';

import { EButtonSizes, EButtonTypes } from 'app/shared/enums/button.enums';
import {
  EMediaLightboxUsage,
  EPromptUsage,
  ELocalStorageKey
} from '../../shared/enums/shared.enums';
import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { EToastType, EToastUsage } from '../../shared/enums/toast.enums';
import { ECreatorMediaTypes } from 'app/enums/creator.enums';
import { EMediaFilter } from 'app/enums/media.enums';
import { EFeatureGates } from '../../shared/enums/feature-gates.enums';
import { EImageUsage } from 'app/shared/enums/image-usage.enums';

import { slideInOut } from '../../shared/animations/slideInOut.animations';
import { fadeAnimation, fadeAnimationSequence } from '../../shared/animations/fade.animations';

@Component({
  selector: 'stxt-image-generation-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    SvgIconComponent,
    CreatorsCarouselComponent,
    SuggestedPromptComponent,
    ImagePresetsComponent,
    ClickOutsideDirective,
    ThumbnailLoaderComponent,
    ToastComponent,
    MediaLightboxComponent,
    EmptyStateComponent,
    ImageComponent,
    NgbTooltip
  ],
  providers: [NgbTooltipConfig],
  templateUrl: './image-generation-page.component.html',
  styleUrl: './image-generation-page.component.scss',
  animations: [slideInOut, fadeAnimation, fadeAnimationSequence]
})
export class ImageGenerationPageComponent implements OnInit, OnDestroy {
  @ViewChild('presets') presets: ImagePresetsComponent;
  public creators: IDiscoverItem[] = this.discoverService.creators$.getValue();
  public tokenSubscription: Subscription;
  public allMediaSubscription: Subscription;
  public tokens: number;
  public selectedCreator: IDiscoverItem;
  public imageGalleryIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'image-gen-gallery' };
  public filterIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'filter-list' };
  public arrowIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'arrow_left' };
  public tokenIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'token-star' };
  public generateButton: IButtonConfig = {
    text: 'Generate',
    buttonSize: EButtonSizes.Default,
    fill: EButtonTypes.SecondaryFilled
  };
  public galleryButtonConfig: IButtonConfig = {
    fill: EButtonTypes.Empty,
    buttonSize: EButtonSizes.Default
  };
  public arrowRightIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'arrow_right_full' };
  public isRightPanelVisible: boolean = false;
  public isShowSuggestions: boolean = false;
  public isToastMessage: boolean = false;
  public isShowFilter: boolean = false;
  public mediaFullScreen: boolean = false;
  public isGeneratingImage: boolean = false;
  public suggestedPrompts: string[] = [];
  public filteredPrompts: string[] = [];
  public isAdminAccessEnabled: boolean = false;
  public isJustAddedEnabled: boolean = false;
  public isUnifiedMediaGeneration: boolean = false;
  public promptSuggestions: IPrompt[] = [];
  public userInput: string = '';
  public generatePrice: number = 10;
  public creatorsFilterList: string[] = [];
  public availableCreatorsFilters: IDiscoverItem[] = [];
  public imagesToDisplay: IGenerateImage[] = [];
  public justAddedImages: IGenerateImage[] = [];
  public myImagesData: IGenerateImage[] = [];
  public toast: IToastConfig;
  public selectedMedia: IGenerateImage;
  public selectedJustAddedMedia: IGenerateImage;
  public postDescription: string = 'My images';
  public userId: string = localStorage.getItem(ELocalStorageKey.UserId);
  public activeFilter: EMediaFilter = EMediaFilter.All;
  public pendingGenerations: number = 0;
  private readonly TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
  private analyticsPresetData: {
    scene: string | null;
    activity: string | null;
    outfit: string | null;
    accessories: string | null;
    pose: string | null;
  } = { scene: null, activity: null, outfit: null, accessories: null, pose: null };
  private allMedia: IGenerateImage[] = [];
  protected readonly EPromptUsage = EPromptUsage;
  protected readonly EMediaLightboxUsage = EMediaLightboxUsage;
  protected readonly EMediaFilter = EMediaFilter;
  protected readonly EImageUsage = EImageUsage;

  constructor(
    private readonly modalService: NgbModal,
    private readonly analyticsService: AnalyticsService,
    private readonly discoverService: DiscoverService,
    private readonly conversationService: ConversationService,
    private readonly mediaService: MediaService,
    private readonly tokensService: TokensService,
    private readonly statsigService: StatsigService,
    private readonly profileStateService: ProfileStateService
  ) {}

  public emptyStateData: IEmptyStateConfig = {
    svgName: 'images',
    heading: 'Bring ideas to life',
    subheading: "Choose a creator and describe what you'd like to see",
    buttonText: 'Generate images'
  };

  ngOnInit(): void {
    this.initializeCreatorsAndTokens();
    this.initImageData();
    this.updateImageData();
    this.loadRecentlyGeneratedImages();
    this.checkStatsigFeatureGate();
  }

  private loadRecentlyGeneratedImages(): void {
    const storedImages = localStorage.getItem(ELocalStorageKey.RecentlyGeneratedImages);
    if (!storedImages) return;

    const parsedImages = JSON.parse(storedImages);
    const now = Date.now();

    // Filter out images older than 24 hours
    const recentImages = parsedImages.filter(
      (item: { image: IGenerateImage; timestamp: number }) => {
        return now - item.timestamp <= this.TWENTY_FOUR_HOURS;
      }
    );

    // Update localStorage with filtered images
    if (recentImages.length !== parsedImages.length) {
      localStorage.setItem(ELocalStorageKey.RecentlyGeneratedImages, JSON.stringify(recentImages));
    }

    // Update justAddedImages with recent images
    this.justAddedImages = recentImages.map((item: { image: IGenerateImage }) => item.image);
    this.selectedJustAddedMedia = this.justAddedImages[0] || null;
  }

  get canAffordGeneration(): boolean {
    return this.tokens - this.pendingGenerations * this.generatePrice >= this.generatePrice;
  }

  initializeCreatorsAndTokens(): void {
    this.creators === null
      ? this.fetchCreatorsList()
      : this.sortCreators(this.creators, this.isAdminAccessEnabled);
    this.tokenSubscription = this.tokensService.tokensBalance$.subscribe(balance => {
      this.tokens = balance;
    });
  }

  initImageData(): void {
    this.allMedia = this.mediaService.allMedia$.getValue();
    if (this.allMedia.length === 0) return;
    this.myImagesData = this.allMedia;
    this.applyFiltersToImages(this.allMedia);
    if (!this.creators?.length) return;
    this.initCreatorsFilterList();
  }

  async checkStatsigFeatureGate(): Promise<void> {
    try {
      await this.statsigService.waitForInitialization();
      this.isAdminAccessEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.ADMIN_ACCESS
      );
      this.isJustAddedEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.JUST_ADDED_MEDIA
      );
      this.isUnifiedMediaGeneration = await this.statsigService.checkFeatureGate(
        EFeatureGates.UNIFIED_MEDIA_GENERATION_SERVICE
      );
    } catch {
      this.isAdminAccessEnabled = false;
      this.isJustAddedEnabled = false;
      this.isUnifiedMediaGeneration = false;
    }
  }

  updateImageData(): void {
    this.allMediaSubscription = this.mediaService.allMedia$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          // Only update if the length or IDs have changed
          if (prev.length !== curr.length) return false;
          return prev.every(
            (item, index) => item.id === curr[index].id && item.url === curr[index].url
          );
        })
      )
      .subscribe(allMedia => {
        this.allMedia = allMedia;
        this.imagesToDisplay.length === 0
          ? this.applyFiltersToImages(allMedia)
          : this.updateImagesPreservingAnimation(allMedia);
        if (this.creators?.length && !this.availableCreatorsFilters?.length)
          this.initCreatorsFilterList();
      });
  }

  fetchCreatorsList(): void {
    this.discoverService
      .getDiscoverData()
      .pipe(first())
      .subscribe({
        next: (response: IDiscoverResponse) => {
          this.sortCreators(response.creators, this.isAdminAccessEnabled);
        }
      });
  }

  sortCreators(creators: IDiscoverItem[], isAdminAccessEnabled: boolean): void {
    this.creators = creators;
    this.creators = this.creators
      .filter(creator => creator.ready || isAdminAccessEnabled)
      .sort((a, b) => a.name.localeCompare(b.name));
    this.selectedCreator = this.creators[0];
    this.getCreatorPrompt();
    if (!this.myImagesData?.length || this.availableCreatorsFilters?.length) return;
    this.initCreatorsFilterList();
  }

  selectImage(image: IGenerateImage, event?: KeyboardEvent): void {
    if (!event) {
      this.selectedJustAddedMedia = image;
      return;
    }

    const currentIndex = this.justAddedImages.indexOf(this.selectedJustAddedMedia);

    switch (event.key) {
      case 'ArrowLeft':
        if (currentIndex > 0) {
          this.selectedJustAddedMedia = this.justAddedImages[currentIndex - 1];
        }
        return;

      case 'ArrowRight':
        if (currentIndex < this.justAddedImages.length - 1) {
          this.selectedJustAddedMedia = this.justAddedImages[currentIndex + 1];
        }
        return;

      default:
        this.selectedJustAddedMedia = image;
    }
  }

  toggleRightPanel(): void {
    this.isRightPanelVisible = !this.isRightPanelVisible;
  }

  showFilters(event: Event): void {
    event.stopPropagation();
    this.isShowFilter = !this.isShowFilter;
  }

  selectFilter(filter: EMediaFilter): void {
    this.activeFilter = filter;
  }

  filterImagesByCreator(creator: IDiscoverItem, event?: Event): void {
    event?.stopPropagation();
    this.activeFilter = EMediaFilter.All;
    const index = this.creatorsFilterList.indexOf(creator.id);
    index === -1
      ? this.creatorsFilterList.push(creator.id)
      : this.creatorsFilterList.splice(index, 1);

    // Create new array with reset loaded states
    const newImages = this.allMedia.map(img => ({
      ...img,
      isLoaded: false
    }));

    this.applyFiltersToImages(newImages);
  }

  isChecked(creatorId: string): boolean {
    return this.creatorsFilterList.includes(creatorId);
  }

  onPresetPrompt(presetData: IPresetData): void {
    if (!presetData) return;

    const preset = presetData.preset.toLowerCase();
    const existingPrompts = this.userInput.split(' ');
    const filteredPrompts = existingPrompts.filter(word => !this.suggestedPrompts?.includes(word));
    this.userInput = [...filteredPrompts, presetData.selectedImage.prompt]
      .filter(Boolean)
      .join(' ')
      .trim();

    const isPresetKey = preset in this.analyticsPresetData;
    if (isPresetKey) {
      this.analyticsPresetData[preset as keyof typeof this.analyticsPresetData] =
        presetData.selectedImage.prompt;
    }
  }

  updateSelectedCreator(creator: IDiscoverItem): void {
    this.selectedCreator = creator;
    this.getCreatorPrompt();
  }

  getCreatorPrompt(): void {
    this.conversationService
      .getCreatorPrompt(this.selectedCreator.id)
      .pipe(first())
      .subscribe({
        next: (res: ICreatorPromptResponse) => {
          this.promptSuggestions = res.prompt_suggestions;
          this.suggestedPrompts = res.prompt_suggestions.map(prompt => prompt.short_prompt);
          this.filteredPrompts = res.prompt_suggestions.map(prompt => prompt.short_prompt);
        }
      });
  }

  generateImage(): void {
    if (!this.userInput.length) return;

    if (this.tokens < this.generatePrice) {
      this.tokensService.getMoreTokensEvent$.next(true);
      return;
    }

    if (!this.canAffordGeneration) {
      this.tokensService.getMoreTokensEvent$.next(true);
      return;
    }

    this.pendingGenerations++;
    this.isGeneratingImage = true;
    this.isRightPanelVisible = true;
    this.handleToast(EToastUsage.IMAGE_GENERATING);
    this.updateAvailableCreatorsFilters(this.selectedCreator);

    const selectedPrompt = this.promptSuggestions.find(p => p.short_prompt === this.userInput);
    const prompt = selectedPrompt?.short_prompt || this.userInput;

    // Create a pending image immediately
    const pendingImage: IGenerateImage = {
      id: `${Date.now()}`,
      url: '',
      prompt: prompt,
      generated: false,
      creator_id: this.selectedCreator.id
    };

    // Add pending image to justAddedImages immediately
    this.justAddedImages = [pendingImage, ...this.justAddedImages];
    this.selectedJustAddedMedia = pendingImage;

    this.userInput = '';
    this.presets.resetPresets();
    this.mediaService
      .generateImage(
        {
          prompt,
          creator_id: this.selectedCreator.id,
          media_type: ECreatorMediaTypes.Image,
          origin: 'standalone'
        },
        this.isUnifiedMediaGeneration
      )
      .pipe(
        first(),
        switchMap(res => {
          const generatedImage = res.items[0];
          // Replace the pending image with the real one
          this.justAddedImages = this.justAddedImages.map(img =>
            img.id === pendingImage.id ? { ...generatedImage, generated: false } : img
          );
          this.selectedJustAddedMedia = this.justAddedImages[0];

          // Store in localStorage
          const storedImages = localStorage.getItem(ELocalStorageKey.RecentlyGeneratedImages);
          const existingImages = storedImages ? JSON.parse(storedImages) : [];
          const newImageEntry = {
            image: { ...generatedImage, generated: false },
            timestamp: Date.now()
          };
          localStorage.setItem(
            ELocalStorageKey.RecentlyGeneratedImages,
            JSON.stringify([newImageEntry, ...existingImages])
          );

          return this.tokensService.fetchTokensBalance(true).pipe(map(() => res));
        })
      )
      .subscribe({
        next: () => {
          this.pendingGenerations--;
          this.isGeneratingImage = false;
          this.trackEvent();
          this.profileStateService.refreshTransactions();
        },
        error: err => {
          this.updateImagesListOnError(pendingImage);
          err.status === 400
            ? this.handleToast(EToastUsage.IMAGE_GENERATION_ERROR)
            : this.handleToast(EToastUsage.IMAGE_GENERATION_FAILED);
        }
      });
  }

  updateImagesListOnError(pendingImage: IGenerateImage): void {
    this.pendingGenerations--;
    this.isGeneratingImage = false;

    this.justAddedImages = this.justAddedImages.filter(img => img.id !== pendingImage.id);
    if (this.justAddedImages.length > 0) {
      this.selectedJustAddedMedia = this.justAddedImages[0];
    }

    const index = this.imagesToDisplay.findIndex(img => img.id === pendingImage.id);
    if (index !== -1) {
      this.imagesToDisplay.splice(index, 1);
    }
  }

  onImageGenerated(image: IGenerateImage): void {
    this.handleToast(EToastUsage.IMAGE_GENERATED);

    // Update the image in imagesToDisplay
    const displayImage = this.imagesToDisplay.find(i => i.id === image.id);
    if (displayImage) {
      displayImage.generated = true;
    }

    // Update the image in MediaService cache
    const mediaImage = this.allMedia.find(i => i.id === image.id);
    if (!mediaImage) return;

    mediaImage.generated = true;
    this.mediaService.addOrUpdateFetchedMedia({ ...mediaImage });

    // Update the image in justAddedImages
    this.justAddedImages = this.justAddedImages.map(img =>
      img.id === this.selectedJustAddedMedia.id ? { ...img, generated: true } : img
    );

    // Update localStorage
    this.updateLocalStorageImage(image);
  }

  private updateLocalStorageImage(image: IGenerateImage): void {
    const storedImages = localStorage.getItem(ELocalStorageKey.RecentlyGeneratedImages);
    if (!storedImages) return;

    const parsedImages = JSON.parse(storedImages);
    const updatedImages = parsedImages.map((item: { image: IGenerateImage; timestamp: number }) => {
      if (item.image.id === image.id) {
        return {
          ...item,
          image: { ...item.image, generated: true }
        };
      }
      return item;
    });
    localStorage.setItem(ELocalStorageKey.RecentlyGeneratedImages, JSON.stringify(updatedImages));
  }

  trackEvent(): void {
    const { scene, activity, outfit, accessories, pose } = this.analyticsPresetData;

    this.analyticsService.imageGenerated(
      this.selectedCreator.id,
      this.selectedCreator.name,
      this.userId,
      'generatePage',
      scene,
      activity,
      outfit,
      accessories,
      pose
    );
  }

  handleToast(toastUsage: EToastUsage): void {
    this.isToastMessage = true;

    switch (toastUsage) {
      case EToastUsage.IMAGE_GENERATED:
        this.toast = {
          toastHeading: 'Your image is ready',
          toastSubheading: `You have ${this.tokens} tokens left.`,
          toastType: EToastType.SUCCESS
        };
        break;
      case EToastUsage.IMAGE_GENERATING:
        this.toast = {
          toastHeading: 'Your image is on the way',
          toastSubheading: `Please stay on this page as we create your image.`,
          toastType: EToastType.WARN
        };
        break;
      case EToastUsage.IMAGE_GENERATION_FAILED:
        this.toast = {
          toastHeading: 'Image could not be generated',
          toastType: EToastType.FAIL
        };
        break;
      case EToastUsage.IMAGE_GENERATION_ERROR: {
        const contentRating = this.selectedCreator.not_safe_for_work ? 'NSFW' : 'SFW';
        this.toast = {
          toastHeading: `${this.selectedCreator.name} can generate ${contentRating} images only.`,
          toastSubheading: 'Please try a different prompt or choose another creator.',
          toastType: EToastType.ERROR
        };
        break;
      }
    }
  }

  openMediaFullScreen(selectedMedia: IGenerateImage): void {
    this.selectedMedia = selectedMedia;
    this.mediaFullScreen = true;

    this.analyticsService.mediaClicked(
      this.userId,
      this.selectedCreator.name,
      '',
      this.selectedCreator.id,
      selectedMedia.id,
      ECreatorMediaTypes.Image
    );
  }

  openRegenerateModal(selectedMedia: IGenerateImage): void {
    this.selectedMedia = selectedMedia;
    const regenerateModal = this.modalService.open(RegenerateImageModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    regenerateModal.closed.subscribe((next: string) => {
      if (next !== 'regenerate') return;
      this.confirmRegenerate();
    });
  }

  confirmRegenerate(): void {
    const targetImage = this.imagesToDisplay.find(image => image.id === this.selectedMedia.id);
    this.mediaFullScreen = false;
    this.handleToast(EToastUsage.IMAGE_GENERATING);

    if (targetImage) {
      targetImage.id = this.selectedMedia.id;
      this.handleToast(EToastUsage.IMAGE_GENERATED);
    }
  }

  onInputChange(input: string): void {
    const trimmedInput = input.trim().toLowerCase();
    this.userInput = trimmedInput;
    this.filteredPrompts = trimmedInput
      ? this.suggestedPrompts?.filter(prompt => prompt.toLowerCase().includes(trimmedInput))
      : [...this.suggestedPrompts];
  }

  selectPrompt(suggestedItem: string): void {
    this.userInput = suggestedItem;
    this.isShowSuggestions = false;
  }

  // Helper to apply filters to images - simplified since MediaLoaderComponent handles loading states
  applyFiltersToImages(allMedia: IGenerateImage[]): void {
    this.myImagesData = allMedia;
    this.imagesToDisplay =
      this.creatorsFilterList.length === 0
        ? [...allMedia]
        : allMedia.filter(imageFile => this.creatorsFilterList.includes(imageFile.creator_id));
    this.updateAvailableCreatorsFiltersFromMedia(allMedia);
  }

  updateImagesPreservingAnimation(allMedia: IGenerateImage[]): void {
    const filteredMedia =
      this.creatorsFilterList.length === 0
        ? allMedia
        : allMedia.filter(img => this.creatorsFilterList.includes(img.creator_id));

    const hasChanges = filteredMedia.some(newImg => {
      const existingImg = this.imagesToDisplay.find(i => i.id === newImg.id);
      return !existingImg || existingImg.url !== newImg.url;
    });

    if (!hasChanges) return;

    this.imagesToDisplay = [...filteredMedia];
    this.updateAvailableCreatorsFiltersFromMedia(allMedia);
  }

  updateAvailableCreatorsFiltersFromMedia(mediaData: IGenerateImage[]): void {
    if (!this.creators?.length) return;

    // Process all media to find creators
    for (const image of mediaData) {
      if (!image?.creator_id) continue;
      const creatorExists = this.availableCreatorsFilters.some(c => c.id === image.creator_id);
      if (creatorExists) continue;
      const creator = this.creators.find(c => c.id === image.creator_id);
      if (!creator) continue;
      this.availableCreatorsFilters.push(creator);
    }
    if (this.isGeneratingImage && this.selectedCreator) {
      this.updateAvailableCreatorsFilters(this.selectedCreator);
    }
  }

  initCreatorsFilterList(): void {
    this.availableCreatorsFilters = [];

    for (const image of this.allMedia) {
      if (!this.availableCreatorsFilters.some(c => c.id === image.creator_id)) {
        const creator = this.creators?.find(c => c.id === image.creator_id);
        if (!creator) continue;
        this.availableCreatorsFilters.push(creator);
      }
    }
  }

  updateAvailableCreatorsFilters(creator: IDiscoverItem): void {
    if (!this.availableCreatorsFilters.some(c => c.id === creator.id)) {
      this.availableCreatorsFilters.push(creator);
    }
  }

  ngOnDestroy(): void {
    this.tokenSubscription.unsubscribe();
    this.allMediaSubscription?.unsubscribe();
    this.tokensService.getMoreTokensEvent$.next(false);
  }
}
